/**
 * Locale utilities for consistent locale handling across the application.
 *
 * The locale maps live in `@/lib/format`; the exports here delegate so that older
 * call sites and new ones agree on `de` -> `de-CH`, `en` -> `en-CH`, `pt` -> `pt-PT`.
 */

import type { Locale } from 'date-fns';
import { dateFnsLocales, getDateFnsLocale as getFormatDateFnsLocale, getLocaleTag } from '@/lib/format';

/**
 * Convert a Lingui locale to the BCP 47 tag `Intl` and `toLocale*String` format in.
 *
 * @deprecated Use `getLocaleTag` from `@/lib/format`, or a `format*` helper there.
 * @param locale - The Lingui locale (e.g., 'en', 'de', 'fr')
 * @returns The tag (e.g., 'en-CH', 'de-CH', 'pt-PT'); unknown locales give 'en-CH'
 */
export function getBrowserLocaleString(locale: string): string {
  return getLocaleTag(locale);
}

/**
 * date-fns locale objects keyed by Lingui locale (`en` is British English, day first).
 */
export const dateFnsLocaleMap: Readonly<Record<string, Locale>> = dateFnsLocales;

/**
 * Get date-fns locale from Lingui locale code
 * @param linguiLocale - Lingui locale code (e.g., 'en', 'de')
 * @returns Corresponding date-fns Locale object, defaults to British English
 */
export function getDateFnsLocale(linguiLocale: string): Locale {
  return getFormatDateFnsLocale(linguiLocale);
}

/**
 * Get the day the week starts on.
 *
 * INTENTIONALLY LOCALE-INDEPENDENT: always Monday (1), in every locale.
 * This is a product decision, not a formatting default. The app is a Swiss
 * product where the week starts on Monday; date-fns' `enUS` locale would
 * otherwise report Sunday (0), which made an English-locale user see a
 * Monday-start date picker next to a Sunday-start event calendar, capacity
 * week view and workforce dashboard (#956).
 *
 * Kept as a function (rather than inlining `1` at the call sites) so the
 * decision has one greppable home.
 *
 * @param _linguiLocale - Lingui locale code; accepted for call-site
 *   compatibility and deliberately ignored.
 * @returns Always 1 (Monday)
 */
export function getWeekStartsOn(_linguiLocale: string): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
  return 1;
}

/**
 * Format a Date to YYYY-MM-DD using local timezone (no UTC conversion).
 * Prevents timezone drift when sending dates to the server.
 * @param date - Date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateLocalYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Compute the start of week for a given date, respecting the locale's week start.
 * All computation is done in local timezone.
 * @param date - Date to compute week start for
 * @param weekStartsOn - Day of week that starts the week (0=Sunday, 1=Monday, etc.)
 * @returns Date object representing the start of the week
 */
export function startOfWeekLocal(date: Date, weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setHours(0, 0, 0, 0);
  const currentWeekday = result.getDay();
  const offset = (currentWeekday - weekStartsOn + 7) % 7;
  result.setDate(result.getDate() - offset);
  return result;
}